import { ref, onUnmounted } from 'vue';
import { useNotifications } from './useNotifications';

/**
 * Composable for polling AI suggestion status as a fallback when Echo events are missed.
 *
 * Jobs write their result into model metadata (e.g. File.column_metadata.ai_suggestions,
 * DataSet.settings.ai_filter_suggestion_metadata). We poll until that result appears,
 * not updated_at. After 60 seconds we assume failure.
 */
export function useAISuggestionPolling() {
    const { showNotification } = useNotifications();

    // Map<requestKey, { intervalId, timeoutId, startTime, onSuccess, onTimeout, checkFn }>
    const activePolling = ref(new Map());

    /**
     * Start polling for an AI suggestion completion
     *
     * @param {Object} options - Polling options
     * @param {string} options.requestKey - Unique key for this polling request
     * @param {Function} options.checkFn - Async function that returns { done, hasResult, data } — done when job has written result; hasResult when result is usable (e.g. proposal present)
     * @param {Function} options.onSuccess - Callback when polling detects result (done && hasResult)
     * @param {Function} options.onTimeout - Callback when polling times out
     * @param {Function} options.onNoResult - Callback when done but no usable result (optional)
     * @param {number} options.pollInterval - Polling interval in ms (default: 15000)
     * @param {number} options.timeout - Timeout in ms (default: 60000)
     */
    function startPolling({
        requestKey,
        checkFn,
        onSuccess,
        onTimeout,
        onNoResult = null,
        pollInterval = 15000,
        timeout = 90000
    }) {
        if (activePolling.value.has(requestKey)) {
            stopPolling(requestKey);
        }

        const startTime = Date.now();

        const intervalId = setInterval(async () => {
            try {
                const result = await checkFn();

                if (!result || !result.done) {
                    return;
                }

                stopPolling(requestKey);

                if (result.hasResult) {
                    if (onSuccess) {
                        onSuccess(result.data);
                    }
                } else {
                    if (onNoResult) {
                        onNoResult(result.data);
                    } else {
                        showNotification({
                            icon: '/img/icons/avid-icons/danger.svg',
                            text: result.error || 'Something went wrong with the AI suggestion. Please try again.',
                            type: 'error'
                        });
                        if (onTimeout) {
                            onTimeout();
                        }
                    }
                }
            } catch (error) {
                console.error(`Polling error for ${requestKey}:`, error);
            }
        }, pollInterval);

        const timeoutId = setTimeout(() => {
            if (activePolling.value.has(requestKey)) {
                stopPolling(requestKey);

                showNotification({
                    icon: '/img/icons/avid-icons/danger.svg',
                    text: 'The AI suggestion is taking longer than expected. You can continue to wait or you can click the button to try again.',
                    type: 'error'
                });

                if (onTimeout) {
                    onTimeout();
                }
            }
        }, timeout);

        activePolling.value.set(requestKey, {
            intervalId,
            timeoutId,
            startTime,
            onSuccess,
            onTimeout,
            checkFn
        });
    }

    /**
     * Stop polling for a specific request
     * @param {string} requestKey - The unique key for the polling request
     */
    function stopPolling(requestKey) {
        const polling = activePolling.value.get(requestKey);
        if (polling) {
            clearInterval(polling.intervalId);
            clearTimeout(polling.timeoutId);
            activePolling.value.delete(requestKey);
        }
    }

    /**
     * Stop all active polling
     */
    function stopAllPolling() {
        for (const [key] of activePolling.value) {
            stopPolling(key);
        }
    }

    /**
     * Check if currently polling for a specific request
     * @param {string} requestKey - The unique key for the polling request
     * @returns {boolean}
     */
    function isPolling(requestKey) {
        return activePolling.value.has(requestKey);
    }

    // Cleanup on component unmount
    onUnmounted(() => {
        stopAllPolling();
    });

    return {
        startPolling,
        stopPolling,
        stopAllPolling,
        isPolling,
        activePolling
    };
}

/**
 * Helper to create a check function for file-based AI suggestions.
 * Jobs write into File.column_metadata: ai_filter_suggestion or ai_suggestions[destinationColumn].
 * We detect completion by the presence of that result, not updated_at.
 */
export function createFileCheckFunction({ clientUrl, fileId, type, destinationColumn = null }) {
    return async () => {
        try {
            const response = await window.axios.get(`/api/file-mapping/${clientUrl}/${fileId}/status`);
            const data = response.data;

            if (type === 'filter') {
                const filterSuggestion = data.ai_filter_suggestion;
                const hasResult = filterSuggestion && filterSuggestion.proposal;
                // Job only writes ai_filter_suggestion when it has a valid proposal
                if (!hasResult) return { done: false };
                return { done: true, hasResult: true, data: filterSuggestion };
            }
            if (type === 'column' && destinationColumn) {
                const suggestions = data.ai_suggestions || {};
                const columnSuggestion = suggestions[destinationColumn];
                const hasResult = columnSuggestion && columnSuggestion.proposal;
                if (!hasResult) return { done: false };
                return { done: true, hasResult: true, data: columnSuggestion };
            }

            return { done: false };
        } catch (error) {
            console.error('File check function error:', error);
            return { done: false };
        }
    };
}

/**
 * Helper to create a check function for dataset-based filter AI suggestions.
 * SuggestFilterJob writes into DataSet.settings.ai_filter_suggestion_metadata.proposal.
 * FilterMappingAIController::get returns data.proposal from that. We detect completion by proposal presence.
 */
export function createDataSetFilterCheckFunction({ clientUrl, dataSetId }) {
    return async () => {
        try {
            const response = await window.axios.get(`/api/filter-mapping/${clientUrl}/${dataSetId}`);
            const data = response.data;
            const proposal = data.proposal;
            if (!proposal) return { done: false };
            return {
                done: true,
                hasResult: true,
                data: {
                    proposal,
                    reasoning: data.reasoning,
                    response_id: data.response_id,
                    data_set: data.data_set
                }
            };
        } catch (error) {
            console.error('DataSet filter check function error:', error);
            return { done: false };
        }
    };
}

/**
 * Helper to create a check function for dataset column AI suggestions.
 * SuggestColumnMappingJob writes into ColumnMapping.ai_suggestion_metadata.proposal (and ColumnMappingSuggestion.result).
 * ColumnMappingAIController::get returns column_mapping with ai_suggestion_metadata.proposal. We detect completion by proposal presence.
 */
export function createDataSetColumnCheckFunction({ clientUrl, columnMappingId }) {
    return async () => {
        try {
            const response = await window.axios.get(`/api/column-mapping/${clientUrl}/${columnMappingId}`);
            const data = response.data;
            const proposal = data.column_mapping?.ai_suggestion_metadata?.proposal;
            if (!proposal) return { done: false };
            return { done: true, hasResult: true, data };
        } catch (error) {
            console.error('DataSet column check function error:', error);
            return { done: false };
        }
    };
}

/**
 * Helper to create a check function for column mapping suggestion by ID (suggestion-only flow, no column mapping yet).
 * GET /api/column-mapping/{client}/suggestion/{suggestionId} returns column_mapping (synthetic) with proposal when status=ready.
 */
export function createColumnMappingSuggestionCheckFunction({ clientUrl, suggestionId }) {
    return async () => {
        try {
            const response = await window.axios.get(`/api/column-mapping/${clientUrl}/suggestion/${suggestionId}`);
            const data = response.data;
            const proposal = data.column_mapping?.ai_suggestion_metadata?.proposal;
            if (!proposal) return { done: false };
            return { done: true, hasResult: true, data };
        } catch (error) {
            console.error('Column mapping suggestion check function error:', error);
            return { done: false };
        }
    };
}

/**
 * Helper to create a check function for segment suggestion AI.
 * SuggestSegmentJob writes into SegmentSuggestion (name, value_prop, filter, status=ready).
 * SegmentSuggestionController::get returns segment_suggestion. We detect completion by status === 'ready'.
 */
export function createSegmentSuggestionCheckFunction({ clientUrl, segmentSuggestionId }) {
    return async () => {
        try {
            const response = await window.axios.get(`/api/segment-suggestion/${clientUrl}/${segmentSuggestionId}`);
            const data = response.data;
            const suggestion = data.segment_suggestion;
            if (!suggestion || suggestion.status !== 'ready') return { done: false };
            return {
                done: true,
                hasResult: true,
                data: suggestion
            };
        } catch (error) {
            const message = error.response?.data?.error || error.response?.data?.message;
            if (error.response?.status === 404 && message) {
                return { done: true, hasResult: false, error: message };
            }
            console.error('Segment suggestion check function error:', error);
            return { done: false };
        }
    };
}
